import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PartOfServiceGlobal } from '@tacliatech/types';
import { ImageFirebasePipe } from '@web-frontend/shared/pipes/image/image-firebase.pipe';

export interface DealImageItem {
  filename: string;
  source: 'deal' | 'evidence';
  thumbnailUrl: string;
  selected: boolean;
}

@Component({
  selector: 'roma-deal-image-selector',
  templateUrl: './deal-image-selector.component.html',
  styleUrls: ['./deal-image-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ImageFirebasePipe],
})
export class DealImageSelectorComponent implements OnChanges {
  @Input() dealFiles: Array<{
    extension?: string;
    fileUploadService?: string;
    name?: string;
  }> = [];

  @Input() evidences: string[] = [];

  @Input() selectedImages: PartOfServiceGlobal.SelectedDealImage[] = [];

  @Output() selectionChange = new EventEmitter<
    PartOfServiceGlobal.SelectedDealImage[]
  >();

  items: DealImageItem[] = [];

  constructor(
    private imageFirebasePipe: ImageFirebasePipe,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const filesOrEvidencesChanged = changes.dealFiles || changes.evidences;
    const onlySelectionChanged =
      !filesOrEvidencesChanged && changes.selectedImages;

    if (
      filesOrEvidencesChanged ||
      (changes.selectedImages && this.items.length === 0)
    ) {
      this.buildItems();
    } else if (onlySelectionChanged) {
      this.applySelectionToItems();
    }
  }

  toggleItem(item: DealImageItem): void {
    item.selected = !item.selected;
    this.emitSelection();
    this.cdRef.markForCheck();
  }

  private applySelectionToItems(): void {
    const selectedSet = new Set(
      (this.selectedImages || []).map((s) => `${s.source}::${s.filename}`)
    );
    this.items.forEach((item) => {
      item.selected = selectedSet.has(`${item.source}::${item.filename}`);
    });
    this.cdRef.markForCheck();
  }

  private buildItems(): void {
    const selectedSet = new Set(
      (this.selectedImages || []).map((s) => `${s.source}::${s.filename}`)
    );

    const dealItems: DealImageItem[] = (this.dealFiles || [])
      .filter((f) => !!f.fileUploadService)
      .map((f) => {
        const filename = f.fileUploadService;
        return {
          filename,
          source: 'deal' as const,
          thumbnailUrl: this.imageFirebasePipe.transform(filename, 'deal'),
          selected: selectedSet.has(`deal::${filename}`),
        };
      });

    const evidenceItems: DealImageItem[] = (this.evidences || [])
      .filter((e) => !!e)
      .map((filename) => ({
        filename,
        source: 'evidence' as const,
        thumbnailUrl: this.imageFirebasePipe.transform(filename, 'evidence'),
        selected: selectedSet.has(`evidence::${filename}`),
      }));

    this.items = [...dealItems, ...evidenceItems];
    this.cdRef.markForCheck();
  }

  private emitSelection(): void {
    const selected: PartOfServiceGlobal.SelectedDealImage[] = this.items
      .filter((i) => i.selected)
      .map((i) => ({ filename: i.filename, source: i.source }));
    this.selectionChange.emit(selected);
  }
}
